import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { enrollStudent } from "@/hooks/useEnrollment";
import { useToast } from "@/hooks/use-toast";
import { capitalizeText } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

// Import new components
import { EnrollmentProgress } from "./enrollment/EnrollmentProgress";
import { StudentDataStep } from "./enrollment/StudentDataStep";
import { ActivitySelectionStep } from "./enrollment/ActivitySelectionStep";
import { SummaryStep } from "./enrollment/SummaryStep";
import { SuccessStep } from "./enrollment/SuccessStep";

const formSchema = z.object({
  studentFirstName: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  studentFirstSurname: z.string().min(2, "El primer apellido debe tener al menos 2 caracteres"),
  studentSecondSurname: z.string().optional(),
  studentClass: z.string().min(1, "La clase es obligatoria"),
  parentEmail: z.string().email("Email inválido"),
  parentPhone: z.string().min(9, "El teléfono debe tener al menos 9 dígitos"),
  selectedActivities: z.array(z.string()).optional().default([]),
});

type FormData = z.infer<typeof formSchema>;

interface EnrollmentStep {
  step: 'form' | 'activities' | 'configuration' | 'summary' | 'success';
  data?: any;
}

interface ActivityConfig {
  id: string;
  name: string;
  billingType: 'per_session' | 'monthly';
  selectedDays: number[];
  requiresDaySelection: boolean;
}

export function ParentEnrollmentForm() {
  const [enrollmentStep, setEnrollmentStep] = useState<EnrollmentStep>({ step: 'form' });
  const [isLoading, setIsLoading] = useState(false);
  const [activityConfigs, setActivityConfigs] = useState<ActivityConfig[]>([]);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      studentFirstName: "",
      studentFirstSurname: "",
      studentSecondSurname: "",
      studentClass: "",
      parentEmail: "",
      parentPhone: "",
      selectedActivities: [],
    },
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleStudentDataNext = (data: FormData) => {
    setEnrollmentStep({ step: 'activities', data });
    setTimeout(scrollToTop, 100);
  };

  const handleActivityAdd = (config: ActivityConfig) => {
    setActivityConfigs(prev => [...prev, config]);
  };

  const handleActivityRemove = (activityId: string) => {
    setActivityConfigs(prev => prev.filter(config => config.id !== activityId));
  };

  const handleActivitiesNext = () => {
    form.setValue('selectedActivities', activityConfigs.map(config => config.id));
    setEnrollmentStep({ step: 'summary', data: enrollmentStep.data });
    setTimeout(scrollToTop, 100);
  };

  const handleBackToForm = () => {
    setEnrollmentStep({ step: 'form' });
    setTimeout(scrollToTop, 100);
  };

  const handleBackToActivities = () => {
    setEnrollmentStep({ step: 'activities', data: enrollmentStep.data });
    setTimeout(scrollToTop, 100);
  };

  const handleEnrollmentSubmit = async (data: FormData) => {
    setIsLoading(true);
    
    try {
      const enrollmentResults = [];
      let hasErrors = false;
      
      for (const config of activityConfigs) {
        try {
          // Usar hook unificado para ambos tipos de inscripción
          const result = await enrollStudent({
            studentFirstName: capitalizeText(data.studentFirstName),
            studentLastName: `${capitalizeText(data.studentFirstSurname)}${data.studentSecondSurname ? ' ' + capitalizeText(data.studentSecondSurname) : ''}`.trim(),
            studentClass: data.studentClass,
            parentEmail: data.parentEmail,
            parentPhone: data.parentPhone,
            activityId: config.id,
            billingType: config.billingType,
            selectedDays: config.requiresDaySelection ? config.selectedDays : undefined,
          });

          // El hook unificado ya maneja el tipo de facturación durante la creación

          enrollmentResults.push({
            ...result,
            activityName: config.name,
            billingType: config.billingType,
            scheduledDays: config.requiresDaySelection ? config.selectedDays : null
          });

        } catch (error: any) {
          hasErrors = true;
          
          toast({
            title: "Error en inscripción",
            description: `Error inscribiendo en ${config.name}: ${error.message}`,
            variant: "destructive",
          });
        }
      }

      if (enrollmentResults.length > 0) {
        // Enviar email de confirmación
        try {
          const studentName = `${capitalizeText(data.studentFirstName)} ${capitalizeText(data.studentFirstSurname)}${data.studentSecondSurname ? ' ' + capitalizeText(data.studentSecondSurname) : ''}`.trim();
          
          await supabase.functions.invoke('send-enrollment-email', {
            body: {
              studentData: {
                name: studentName,
                class: data.studentClass
              },
              parentData: {
                email: data.parentEmail,
                phone: data.parentPhone
              },
              activitiesData: enrollmentResults.map(result => ({
                name: result.activityName,
                billingType: result.billingType
              }))
            }
          });
        } catch (emailError) {
          // Error sending emails is logged but doesn't stop the process
        }

        setEnrollmentStep({ 
          step: 'success', 
          data: { 
            enrollmentResults,
            studentName: `${capitalizeText(data.studentFirstName)} ${capitalizeText(data.studentFirstSurname)}${data.studentSecondSurname ? ' ' + capitalizeText(data.studentSecondSurname) : ''}`.trim(),
            studentClass: data.studentClass,
            hasErrors
          } 
        });

        setTimeout(scrollToTop, 100);

        toast({
          title: "¡Inscripciones completadas!",
          description: `${enrollmentResults.length} inscripción(es) realizada(s) correctamente`,
        });
      }

    } catch (error: any) {
      toast({
        title: "Error general",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    form.reset();
    setActivityConfigs([]);
    setEnrollmentStep({ step: 'form' });
    setTimeout(scrollToTop, 100);
  };

  return (
    <div className="space-y-6">
      {/* Indicador de progreso */}
      <EnrollmentProgress currentStep={enrollmentStep.step} />

      {/* Paso 1: Datos del estudiante */}
      {enrollmentStep.step === 'form' && (
        <StudentDataStep
          form={form as any}
          onNext={handleStudentDataNext}
          isLoading={isLoading}
        />
      )}

      {/* Paso 2: Selección de actividades */}
      {enrollmentStep.step === 'activities' && (
        <ActivitySelectionStep
          activityConfigs={activityConfigs}
          onActivityAdd={handleActivityAdd}
          onActivityRemove={handleActivityRemove}
          onNext={handleActivitiesNext}
          onBack={handleBackToForm}
        />
      )}

      {/* Paso 3: Resumen y confirmación */}
      {enrollmentStep.step === 'summary' && enrollmentStep.data && (
        <SummaryStep
          form={form as any}
          formData={enrollmentStep.data}
          activityConfigs={activityConfigs}
          onBack={handleBackToActivities}
          onSubmit={handleEnrollmentSubmit}
          isLoading={isLoading}
        />
      )}

      {/* Paso 4: Vista de éxito */}
      {enrollmentStep.step === 'success' && enrollmentStep.data && (
        <SuccessStep
          studentName={enrollmentStep.data.studentName}
          studentClass={enrollmentStep.data.studentClass}
          enrollmentResults={enrollmentStep.data.enrollmentResults}
          hasErrors={enrollmentStep.data.hasErrors}
          onReset={resetForm}
        />
      )}
    </div>
  );
}